import { useMemo, useState, type ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { CheckCircleIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import { Button } from '@rsos/components/capstone/base';
import {
  setIsEditing,
  useGetTowProMunicipalitiesQuery,
  type TowProRequestResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { formatDateWithLocalTimezone } from '../../../../../utils/dateUtilities';
import TowProCancelModal from './TowProCancelModal';
import TowProFormSection from './TowProFormSection';
import styles from './TowProStatus.module.css';
import { TOWPRO_REQUEST_SUBMITTED_LABEL } from './towProStatusUtils';

interface DetailProps {
  label: string;
  children: ReactNode;
}

function Detail({ label, children }: DetailProps): JSX.Element {
  return (
    <div className={styles.detail}>
      <Text size="small" color="primaryText" fontWeight="bold">
        {label}
      </Text>
      <div className={styles.detailValue}>
        <Text size="normal" color="secondaryText">
          {children}
        </Text>
      </div>
    </div>
  );
}

const EMPTY_VALUE = '—';

function orDash(value: string | null | undefined): string {
  return value && value.trim().length > 0 ? value : EMPTY_VALUE;
}

function joinPresent(
  parts: (string | null | undefined)[],
  separator: string,
): string {
  const present = parts.filter(
    (part): part is string =>
      typeof part === 'string' && part.trim().length > 0,
  );
  return present.length > 0 ? present.join(separator) : '';
}

interface TowProStatusProps {
  request: TowProRequestResponse;
}

/**
 * Status page for a submitted tow request: the request details as sent to
 * TowPro plus the ingested status timeline.
 */
function TowProStatus({ request }: TowProStatusProps): JSX.Element {
  const dispatch = useDispatch();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { data: municipalities } = useGetTowProMunicipalitiesQuery();
  const requestData = request.request.data;

  const regionName = useMemo(() => {
    const regions =
      municipalities?.subMunicipalities.flatMap(
        subMunicipality => subMunicipality.regions,
      ) ?? [];
    return regions.find(region => region.id === requestData.regionId)?.name;
  }, [municipalities, requestData.regionId]);

  const requestedAtDisplay = formatDateWithLocalTimezone(
    request.createdAt,
    'HH:mm',
  );

  const timeline = useMemo(() => {
    const statusRows = [...request.statuses]
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map(status => ({
        key: `status-${status.id}`,
        time: formatDateWithLocalTimezone(status.createdAt, 'HH:mm'),
        // Transformed TowPro statuses carry no structured fields, so every
        // ingested status renders as a generic update.
        label: 'Updated',
      }));

    return [
      ...statusRows,
      {
        key: 'requested',
        time: requestedAtDisplay,
        label: TOWPRO_REQUEST_SUBMITTED_LABEL,
      },
    ];
  }, [request.statuses, requestedAtDisplay]);

  const owner = requestData.vehicleOwnerData;
  const ownerName = joinPresent([owner?.firstName, owner?.lastName], ' ');
  const ownerAddress = joinPresent(
    [
      joinPresent([owner?.street, owner?.apt], ', '),
      joinPresent([owner?.city, owner?.state], ', '),
      owner?.zipCode,
    ],
    ', ',
  );
  const vehicleSummary = joinPresent(
    [
      joinPresent(
        [requestData.vehicleYear, requestData.make, requestData.model],
        ' ',
      ),
      requestData.color,
    ],
    ' · ',
  );
  const licensePlate = joinPresent(
    [requestData.licensePlateNumber, requestData.licensePlateState],
    ' · ',
  );
  const officerSummary = joinPresent(
    [requestData.officerName, requestData.badgeNumber],
    ' · ',
  );

  return (
    <div className={styles.status} data-name="towpro-status">
      <div className={styles.header}>
        <Text color="primaryText" size="large" fontWeight="bold">
          Tow Request Status
        </Text>
        <div className={styles.headerMeta}>
          Requested at: {requestedAtDisplay} · TowPro
        </div>
      </div>

      <div className={styles.successBanner} data-name="towpro-status-banner">
        <CheckCircleIcon color="background" height={16} width={16} />
        <Text color="background" size="normal">
          Tow request successful
        </Text>
      </div>

      <Detail label="Address">
        {orDash(requestData.addressInput.address1)}
      </Detail>
      <Detail label="Region">{orDash(regionName)}</Detail>
      <Detail label="Reason">{requestData.reason}</Detail>
      <Detail label="Notes">{orDash(requestData.notes)}</Detail>

      <TowProFormSection title="Vehicle">
        <Detail label="Year / Make / Model">{orDash(vehicleSummary)}</Detail>
        <Detail label="VIN">{orDash(requestData.vin)}</Detail>
        <Detail label="License plate">{orDash(licensePlate)}</Detail>
      </TowProFormSection>

      <TowProFormSection title="Incident & Officer">
        <Detail label="Incident #">{orDash(requestData.incidentNumber)}</Detail>
        <Detail label="Officer · Badge">{orDash(officerSummary)}</Detail>
      </TowProFormSection>

      <TowProFormSection title="Vehicle Owner">
        <Detail label="Name">{orDash(ownerName)}</Detail>
        <Detail label="Phone">{orDash(requestData.addressInput.phone)}</Detail>
        <Detail label="Address">{orDash(ownerAddress)}</Detail>
      </TowProFormSection>

      <Detail label="Related Call">
        {request.callerId
          ? formatInternationalPhoneNumberHuman(request.callerId)
          : EMPTY_VALUE}
      </Detail>

      <TowProFormSection title="Status Updates">
        <div className={styles.timeline} data-name="towpro-status-timeline">
          {timeline.map(item => (
            <div key={item.key} className={styles.timelineRow}>
              <span className={styles.timelineTime}>{item.time}</span>
              <Text size="normal" color="primaryText">
                {item.label}
              </Text>
            </div>
          ))}
        </div>
      </TowProFormSection>

      <div className={styles.buttons}>
        <Button
          grow
          btnType="secondary"
          type="button"
          onClick={() => dispatch(setIsEditing(true))}
          dataName="towpro-status-edit-button"
        >
          Edit Request
        </Button>
        <Button
          grow
          btnType="warning_tertiary"
          type="button"
          onClick={() => setShowCancelModal(true)}
          dataName="towpro-status-cancel-button"
        >
          Cancel Request
        </Button>
      </div>

      <TowProCancelModal
        request={request}
        showModal={showCancelModal}
        onClose={() => setShowCancelModal(false)}
      />
    </div>
  );
}

export default TowProStatus;
